import { CARE_PROFILE_SECTION_IDS } from "./careProfileSections";

const IMPORTANT_CONTEXT_REASONS = {
  [CARE_PROFILE_SECTION_IDS.MEDICATION]: "Helps recognize routine doses, changes, and medication-related notes.",
  [CARE_PROFILE_SECTION_IDS.ALLERGIES_FOOD]: "Keeps urgent allergy and food context easy to find.",
  [CARE_PROFILE_SECTION_IDS.BEHAVIOR_SUPPORT]: "Helps explain patterns, hard moments, and safety needs.",
  [CARE_PROFILE_SECTION_IDS.DAILY_SUPPORT]: "Shows caregivers what helps day to day.",
};

const IMPORTANT_CONTEXT_ORDER = [
  CARE_PROFILE_SECTION_IDS.MEDICATION,
  CARE_PROFILE_SECTION_IDS.ALLERGIES_FOOD,
  CARE_PROFILE_SECTION_IDS.BEHAVIOR_SUPPORT,
  CARE_PROFILE_SECTION_IDS.DAILY_SUPPORT,
];

const hasAnyValue = (value) => {
  if (Array.isArray(value)) return value.some(hasAnyValue);
  if (value && typeof value === "object") return Object.values(value).some(hasAnyValue);
  return String(value || "").trim().length > 0;
};

const getSections = (profileOverview = {}) => [
  ...(profileOverview.coreSections || []),
  ...(profileOverview.adaptiveGroup?.sections || []),
];

export const getCareProfileStarterFields = ({
  name = "",
  age = "",
  dateOfBirth = "",
  selectedConditions = [],
  careContext = {},
} = {}) => [
  {
    id: "name",
    label: "Name",
    isComplete: hasAnyValue(name),
  },
  {
    id: "date_of_birth",
    label: "Date of birth",
    isComplete: hasAnyValue(dateOfBirth) || hasAnyValue(age),
  },
  {
    id: "care_focus",
    label: "Care focus",
    isComplete: hasAnyValue(selectedConditions) || hasAnyValue(careContext.careFocus),
  },
];

export const hasCareProfileStarterBasics = (profile = {}) => (
  getCareProfileStarterFields(profile).every((field) => field.isComplete)
);

export const buildCareProfileGuidance = ({
  mode = "edit",
  stage = "intake",
  name = "",
  age = "",
  dateOfBirth = "",
  selectedConditions = [],
  careContext = {},
  profileOverview = {},
} = {}) => {
  const starterFields = getCareProfileStarterFields({
    name,
    age,
    dateOfBirth,
    selectedConditions,
    careContext,
  });
  const starterComplete = starterFields.every((field) => field.isComplete);
  const sections = getSections(profileOverview);
  const aboutSection = sections.find((section) => section.id === CARE_PROFILE_SECTION_IDS.ABOUT);

  if (!starterComplete) {
    return {
      variant: "starter",
      title: "Start with the basics",
      text: "Name, date of birth, and care focus set up the right profile cards. The care focus chooses prompts for this person's situation.",
      starterFields,
      actions: aboutSection ? [{
        ...aboutSection,
        actionLabel: "Add basics",
        guidanceReason: "Then add medication, allergies, safety, and support details when ready.",
      }] : [],
    };
  }

  const importantActions = IMPORTANT_CONTEXT_ORDER
    .map((sectionId) => sections.find((section) => section.id === sectionId && !section.hasDetails))
    .filter(Boolean)
    .slice(0, 3)
    .map((section) => ({
      ...section,
      actionLabel: `Add ${section.label}`,
      guidanceReason: IMPORTANT_CONTEXT_REASONS[section.id],
    }));

  return {
    variant: "ongoing",
    title: mode === "add" && stage === "setup" ? "You can start logging now" : "Build the profile over time",
    text: "Medication, allergies, safety, and support details help Lifelog connect timeline notes. Add what you know now; the rest can come later.",
    starterFields,
    actions: importantActions,
  };
};
